import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformAccounts, createPlatformAccount, deletePlatformAccount, uploadPlatformAccounts, downloadPlatformAccounts, getPlatformAccountLedger, createPlatformLedgerEntry, getStaff } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, DownloadSimple, UploadSimple, Trash, CurrencyInr } from '@phosphor-icons/react';
import { TotpDisplay } from '../components/TotpDisplay';
import { toast } from 'sonner';

export default function PlatformAccountsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant';
  const isCA = user?.role === 'accountant';

  const [platform, setPlatform] = useState('amazon');
  const [accounts, setAccounts] = useState([]);
  const [columns, setColumns] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], total_spend: 0, total_accounts: 0, amazon_accounts: 0, tataneu_accounts: 0 });
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isCA ? 'ledger' : 'accounts');
  const [selectedStaffId, setSelectedStaffId] = useState('');

  // Forms
  const [addOpen, setAddOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newFields, setNewFields] = useState([{ key: '', value: '' }]);
  const [depositForm, setDepositForm] = useState({ staff_id: '', amount: '', payment_method: 'UPI', reference: '', notes: '', deposit_date: new Date().toISOString().split('T')[0] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { platform };
      if (selectedStaffId) params.staff_id = selectedStaffId;
      const [accRes, ledRes] = await Promise.all([
        !isCA ? getPlatformAccounts(params) : Promise.resolve({ data: { accounts: [], columns: [] } }),
        getPlatformAccountLedger(selectedStaffId ? { staff_id: selectedStaffId } : {}),
      ]);
      const accData = accRes.data || {};
      setAccounts(accData.accounts || accData || []);
      setColumns(accData.columns || []);
      setLedger(ledRes.data || { entries: [], total_spend: 0 });
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data?.filter(s => s.role === 'staff') || []);
      }
    } catch (err) { toast.error('Data load fail'); }
    finally { setLoading(false); }
  }, [platform, selectedStaffId, isAdmin, isCA]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddAccount = async () => {
    const data = {};
    newFields.forEach(f => { if (f.key.trim()) data[f.key.trim()] = f.value; });
    setSubmitting(true);
    try {
      await createPlatformAccount({ platform, data });
      toast.success('Account added!');
      setNewFields([{ key: '', value: '' }]);
      setAddOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add fail'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try { await deletePlatformAccount(id); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error('Delete fail'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await uploadPlatformAccounts(platform, fd);
      toast.success(res.data.message);
      e.target.value = '';
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fail'); }
  };

  const handleDownload = async () => {
    try {
      const params = { platform };
      if (selectedStaffId) params.staff_id = selectedStaffId;
      const res = await downloadPlatformAccounts(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${platform}_accounts.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded!');
    } catch (err) { toast.error('Download fail'); }
  };

  const handleDeposit = async () => {
    if (!depositForm.amount || parseFloat(depositForm.amount) <= 0) { toast.error('Amount zaroori hai'); return; }
    setSubmitting(true);
    try {
      await createPlatformLedgerEntry({
        staff_id: depositForm.staff_id, amount: parseFloat(depositForm.amount),
        transaction_type: 'deposit', payment_method: depositForm.payment_method,
        reference: depositForm.reference, notes: depositForm.notes,
        deposit_date: depositForm.deposit_date,
      });
      toast.success('Deposit entry added!');
      setDepositForm({ staff_id: '', amount: '', payment_method: 'UPI', reference: '', notes: '', deposit_date: new Date().toISOString().split('T')[0] });
      setDepositOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fail'); }
    finally { setSubmitting(false); }
  };

  const addField = () => setNewFields(p => [...p, { key: '', value: '' }]);
  const updateField = (idx, field, val) => setNewFields(p => p.map((f, i) => i === idx ? { ...f, [field]: val } : f));
  const removeField = (idx) => setNewFields(p => p.filter((_, i) => i !== idx));

  const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(v || 0);

  return (
    <div className="space-y-4" data-testid="platform-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Platform Accounts</h1>
          <p className="text-sm text-gray-500">Amazon / TataNeu Account Management</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isCA && (
            <>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="add-account-btn"><Plus size={14} /> Add Account</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add {platform.toUpperCase()} Account</DialogTitle></DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <Label className="text-sm">Platform</Label>
                      <select value={platform} onChange={e => setPlatform(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                        <option value="amazon">Amazon</option>
                        <option value="tataneu">TataNeu</option>
                      </select>
                    </div>
                    <Label className="text-xs font-bold text-gray-600">Account Details (add any fields)</Label>
                    {newFields.map((f, i) => (
                      <div key={`field-${i}-${f.key}`} className="flex gap-2 items-center">
                        <Input value={f.key} onChange={e => updateField(i, 'key', e.target.value)} placeholder="Field name (e.g. Email, Password)" className="flex-1 text-xs h-8" data-testid={`field-key-${i}`} />
                        <Input value={f.value} onChange={e => updateField(i, 'value', e.target.value)} placeholder="Value" className="flex-1 text-xs h-8" data-testid={`field-val-${i}`} />
                        {newFields.length > 1 && <button onClick={() => removeField(i)} className="text-red-400 hover:text-red-600 text-xs">✕</button>}
                      </div>
                    ))}
                    <Button type="button" size="sm" variant="outline" className="text-xs" onClick={addField}>+ Add Field</Button>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={handleAddAccount} disabled={submitting} data-testid="acc-submit">{submitting ? 'Adding...' : 'Add Account'}</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <label className="cursor-pointer">
                <Button size="sm" variant="outline" className="gap-1" asChild><span><UploadSimple size={14} /> Upload Excel</span></Button>
                <input type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={handleUpload} data-testid="upload-accounts" />
              </label>
            </>
          )}
          {!isCA && <Button size="sm" variant="outline" className="gap-1" onClick={handleDownload} data-testid="download-accounts"><DownloadSimple size={14} /> Download Excel</Button>}
          {isAdmin && (
            <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="add-deposit-btn"><CurrencyInr size={14} /> Add Deposit</Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Account Creation Deposit</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-sm">Staff</Label>
                    <select value={depositForm.staff_id} onChange={e => setDepositForm(p => ({...p, staff_id: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" data-testid="dep-staff">
                      <option value="">Select Staff</option>
                      {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-sm">Amount (₹) <span className="text-red-500">*</span></Label><Input type="number" step="0.01" value={depositForm.amount} onChange={e => setDepositForm(p => ({...p, amount: e.target.value}))} placeholder="e.g. 500" data-testid="dep-amount" /></div>
                  <div>
                    <Label className="text-sm">Deposit Date</Label>
                    <Input type="date" value={depositForm.deposit_date} onChange={e => setDepositForm(p => ({...p, deposit_date: e.target.value}))} className="text-sm" data-testid="dep-date" />
                  </div>
                  <div>
                    <Label className="text-sm">Payment Method</Label>
                    <select value={depositForm.payment_method} onChange={e => setDepositForm(p => ({...p, payment_method: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" data-testid="dep-method">
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                  <div><Label className="text-sm">Reference</Label><Input value={depositForm.reference} onChange={e => setDepositForm(p => ({...p, reference: e.target.value}))} placeholder="UPI ref / bank ref" data-testid="dep-ref" /></div>
                  <div><Label className="text-sm">Notes</Label><Input value={depositForm.notes} onChange={e => setDepositForm(p => ({...p, notes: e.target.value}))} placeholder="Optional" data-testid="dep-notes" /></div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleDeposit} disabled={submitting} data-testid="dep-submit">{submitting ? 'Adding...' : 'Add Deposit Entry'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Platform Toggle + Staff Filter + Tab */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-gray-100 p-1 rounded-lg">
          {['amazon', 'tataneu'].map(p => (
            <button key={p} onClick={() => setPlatform(p)} className={`px-4 py-1.5 rounded text-sm font-medium transition ${platform === p ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`} data-testid={`platform-${p}`}>
              {p === 'amazon' ? 'Amazon' : 'TataNeu'}
            </button>
          ))}
        </div>
        {isAdmin && (
          <select value={selectedStaffId} onChange={e => setSelectedStaffId(e.target.value)} className="border rounded px-3 py-1.5 text-sm" data-testid="staff-filter">
            <option value="">All Staff</option>
            {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div className="flex bg-gray-100 p-1 rounded-lg ml-auto">
          {[
            { key: 'accounts', label: `Accounts (${accounts.length})`, hide: isCA },
            { key: 'ledger', label: 'Spend Ledger' },
          ].filter(t => !t.hide).map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} className={`px-4 py-1.5 rounded text-sm font-medium transition ${activeTab === t.key ? 'bg-white shadow text-emerald-700' : 'text-gray-500'}`} data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-400">Total Accounts</p><p className="text-lg font-bold">{ledger.total_accounts}</p></div>
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-400">Amazon</p><p className="text-lg font-bold text-orange-600">{ledger.amazon_accounts}</p></div>
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-400">TataNeu</p><p className="text-lg font-bold text-blue-600">{ledger.tataneu_accounts}</p></div>
        <div className="bg-white border rounded-lg p-3"><p className="text-xs text-gray-400">Total Spend</p><p className="text-lg font-bold text-red-600">{formatCurrency(ledger.total_spend)}</p></div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
      ) : (
        <>
          {/* ═══ ACCOUNTS TAB — Dynamic Columns ═══ */}
          {activeTab === 'accounts' && !isCA && (
            <div className="bg-white border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 text-xs">
                      <TableHead className="py-2 px-2 w-10">#</TableHead>
                      {columns.map(col => (
                        <TableHead key={col} className="py-2 px-2 whitespace-nowrap">{col}</TableHead>
                      ))}
                      <TableHead className="py-2 px-2">Created By</TableHead>
                      <TableHead className="py-2 px-2">Date</TableHead>
                      {columns.some(c => c.toLowerCase().includes('otp') || c.toLowerCase().includes('totp') || c.toLowerCase().includes('secret')) && (
                        <TableHead className="py-2 px-2">Live OTP</TableHead>
                      )}
                      {isAdmin && <TableHead className="py-2 px-2 w-10"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.length === 0 ? (
                      <TableRow><TableCell colSpan={columns.length + 4} className="text-center text-gray-400 py-8">No accounts. Upload Excel ya Add Account karein.</TableCell></TableRow>
                    ) : accounts.map((a, i) => (
                      <TableRow key={a.id} data-testid={`account-row-${i}`}>
                        <TableCell className="text-xs py-1 px-2 text-gray-400">{i + 1}</TableCell>
                        {columns.map(col => (
                          <TableCell key={col} className="text-xs py-1 px-2 max-w-[250px] group relative">
                            <div className="flex items-center gap-1">
                              <span className="font-mono truncate block" title={a.data?.[col] || ''}>{a.data?.[col] || '-'}</span>
                              {a.data?.[col] && (
                                <button
                                  onClick={() => { navigator.clipboard.writeText(a.data[col]); toast.success(`${col} copied!`); }}
                                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity flex-shrink-0"
                                  title="Copy"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                                </button>
                              )}
                            </div>
                          </TableCell>
                        ))}
                        <TableCell className="text-xs py-1 px-2">{a.created_by_name}</TableCell>
                        <TableCell className="text-xs py-1 px-2 whitespace-nowrap">{formatDate(a.created_at)}</TableCell>
                        {columns.some(c => c.toLowerCase().includes('otp') || c.toLowerCase().includes('totp') || c.toLowerCase().includes('secret')) && (
                          <TableCell className="py-1 px-2">
                            {(() => {
                              const otpKey = columns.find(c => c.toLowerCase().includes('otp') || c.toLowerCase().includes('totp') || c.toLowerCase().includes('secret'));
                              const otpVal = otpKey ? a.data?.[otpKey] : null;
                              if (!otpVal || otpVal === '-') return <span className="text-[10px] text-gray-300">—</span>;
                              return <TotpDisplay secret={otpVal} />;
                            })()}
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="py-1 px-2">
                            <button onClick={() => handleDeleteAccount(a.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-account-${i}`}><Trash size={14} /></button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500">
                Total: {accounts.length} accounts ({platform.toUpperCase()}) · {columns.length} columns
              </div>
            </div>
          )}

          {/* ═══ LEDGER TAB ═══ */}
          {activeTab === 'ledger' && (
            <div className="space-y-4">
              {/* Staff-wise Summary — for CA/Admin */}
              {(isCA || isAdmin) && ledger.staff_summary?.length > 0 && (
                <div className="bg-white border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-gray-50 border-b"><h3 className="text-xs font-bold text-gray-600">Staff-wise Spend Summary</h3></div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
                    {ledger.staff_summary.map((s, i) => (
                      <div key={s.staff_name || `staff-${i}`} className="bg-gray-50 border rounded p-2.5">
                        <p className="text-xs font-semibold text-gray-700 truncate">{s.staff_name}</p>
                        <p className="text-base font-bold text-red-600">{formatCurrency(s.total_spend)}</p>
                        <p className="text-[10px] text-gray-400">{s.entry_count} entries</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 text-xs">
                        <TableHead className="py-2 px-3">Deposit Date</TableHead>
                        <TableHead className="py-2 px-3">TXN ID</TableHead>
                        <TableHead className="py-2 px-3">Staff</TableHead>
                        <TableHead className="py-2 px-3">Amount</TableHead>
                        <TableHead className="py-2 px-3">Method</TableHead>
                        <TableHead className="py-2 px-3">Reference</TableHead>
                        <TableHead className="py-2 px-3">Notes</TableHead>
                        <TableHead className="py-2 px-3">Done By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(ledger.entries || []).length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">No spend entries yet</TableCell></TableRow>
                      ) : (ledger.entries || []).map((e, i) => (
                        <TableRow key={e.id} data-testid={`ledger-entry-${i}`}>
                          <TableCell className="text-xs py-1 px-3 whitespace-nowrap font-medium">{e.deposit_date || e.date}</TableCell>
                          <TableCell className="text-xs py-1 px-3 font-mono text-gray-500">{e.transaction_id}</TableCell>
                          <TableCell className="text-xs py-1 px-3">{e.staff_name || '-'}</TableCell>
                          <TableCell className="text-xs py-1 px-3 font-bold text-red-600">{formatCurrency(e.amount)}</TableCell>
                          <TableCell className="text-xs py-1 px-3">{e.payment_method}</TableCell>
                          <TableCell className="text-xs py-1 px-3 font-mono">{e.reference || '-'}</TableCell>
                          <TableCell className="text-xs py-1 px-3 max-w-[150px] truncate">{e.notes || '-'}</TableCell>
                          <TableCell className="text-xs py-1 px-3">{e.done_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500 flex justify-between">
                  <span>Total Entries: {(ledger.entries || []).length}</span>
                  <span className="font-bold text-red-600">Total Spend: {formatCurrency(ledger.total_spend)}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
